import React, { useState, useEffect } from "react";
import {
  Button,
  Space,
  Popconfirm,
  message,
  Modal,
} from "antd";
import DataTable from "../../component/ui/DataTable";
import TableImage from "../../component/ui/TableImage";
import { getImageUrl } from "../../component/SafeImage";
import ProductsSoldServ from "../../service/ProductsSoldServ";
import AddProductSold from "../../component/AddProductSold";
import EmptyState from "../../util/EmptyState";
import {
  ShoppingOutlined,
  HistoryOutlined,
  SearchOutlined,
  UndoOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined
} from "@ant-design/icons";
import "../../css/shops.css";

const ProductsSold = () => {
  const [searchText, setSearchText] = useState("");
  const [soldProducts, setSoldProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addProductSold, setAddProductSold] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [productSoldDetails, setProductSoldDetails] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalSold, setTotalSold] = useState(0);
  const [detailsVisible, setDetailsVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  const fetchSoldProducts = async () => {
    try {
      setLoading(true);
      const params = {
        skip: (page - 1) * pageSize,
        limit: pageSize,
      };
      const response = await ProductsSoldServ.getRecords(params);

      const records = response?.data?.records || [];


      setSoldProducts(records);
      setTotalSold(response?.data?.total || records.length);
    } catch (error) {
      console.error("Error fetching sold products:", error);
      message.error("Failed to fetch products sold");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSoldProducts();
  }, [page, pageSize]);

  const handlePageChange = (page) => {
    setPage(page);
    const pageContent = document.querySelector('.page-content');
    if (pageContent) pageContent.scrollTo({ top: 0, behavior: 'smooth' });
    else window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const onShowSizeChange = (current, size) => setPageSize(size);

  const handleAddRecord = () => {
    setProductSoldDetails(null);
    setIsEditing(false);
    setAddProductSold(true);
  };

  const handleEdit = (record) => {
    setProductSoldDetails(record);
    setIsEditing(true);
    setAddProductSold(true);
  };

  const handleDelete = async (id) => {
    try {
      await ProductsSoldServ.remove(id);
      message.success("Record removed successfully");
      fetchSoldProducts();
    } catch (error) {
      console.error("Error deleting record:", error);
      message.error("Failed to remove record");
    }
  };

  const refreshProductsSold = async (isRefresh) => {
    setAddProductSold(false);
    if (isRefresh) fetchSoldProducts();
  };

  const filteredData = (Array.isArray(soldProducts) ? soldProducts : []).filter((item) =>
    item.productId?.name?.toLowerCase().includes(searchText.toLowerCase()) ||
    item.customerName?.toLowerCase().includes(searchText.toLowerCase())
  );

  const columns = [
    {
      title: "Image",
      key: "image",
      width: 80,
      align: "left",
      render: (_, record) => <TableImage src={getImageUrl(record.productId?.imageUrl)} alt={record.productId?.name} />,
    },
    {
      title: "Product Name",
      dataIndex: ["productId", "name"],
      key: "productName",
      width: 250,
      ellipsis: true,
      render: (name) => <span style={{ fontWeight: 600, color: '#1a1a2e' }}>{name}</span>,
    },
    {
      title: "Total Price",
      dataIndex: "totalPrice",
      key: "totalPrice",
      width: 120,
      align: "right",
      render: (price) => <span style={{ color: '#ff6b00', fontWeight: 700 }}>₹{price?.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>,
    },
    {
      title: "Customer",
      dataIndex: "customerName",
      key: "customerName",
      width: 180,
      align: "left",
      ellipsis: true,
    },
    {
      title: "Actions",
      key: "actions",
      width: 220,
      align: "right",
      render: (_, record) => (
        <Space onClick={(e) => e.stopPropagation()} size="middle">
          {record.isOnline ? (
            <span className="ss-status-badge ss-badge-online">
              <ShoppingOutlined style={{ fontSize: 12 }} /> Online
            </span>
          ) : (
            <span className="ss-status-badge ss-badge-offline">
              Offline
            </span>
          )}
          
          <button 
            className="ss-btn-view-premium"
            onClick={() => {
              setSelectedRecord(record);
              setDetailsVisible(true);
            }}
          >
            <EyeOutlined /> View
          </button>

          {!record.isOnline && (
            <Space size="small">
              <Button
                icon={<EditOutlined />}
                type="link"
                size="small"
                onClick={() => handleEdit(record)}
                style={{ color: '#ff6b00' }}
              />
              <Popconfirm
                title="Remove this record?"
                onConfirm={() => handleDelete(record._id)}
                okText="Yes"
                cancelText="No"
              >
                <Button
                  icon={<DeleteOutlined />}
                  type="link"
                  danger
                  size="small"
                />
              </Popconfirm>
            </Space>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="ss-wrapper">
      <div className="ss-container">
        {/* Header Section */}
        <div className="ss-header">
          <div className="ss-header-left">
            <div className="ss-header-icon" style={{ background: 'linear-gradient(135deg, #fff1f0, #ffccc7)' }}>
              <HistoryOutlined style={{ fontSize: 22, color: '#ff4d4f' }} />
            </div>
            <div>
              <h1 className="ss-title">Products Sold</h1>
              <p className="ss-subtitle">{totalSold} records found</p>
            </div>
          </div>

          <button className="ss-create-btn" onClick={handleAddRecord}>
            <PlusOutlined /> Add Record
          </button>
        </div>

        {/* Filter Section */}
        <div className="ss-filter-bar">
          <div className="ss-filter-field" style={{ flex: 2 }}>
            <span className="ss-filter-label">Search</span>
            <div className="ss-search-wrap">
              <SearchOutlined className="ss-search-icon" />
              <input
                type="text"
                placeholder="Search by product or customer..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="ss-search-input"
              />
            </div>
          </div>

          <div className="ss-filter-reset">
            <button
              className="ss-reset-btn"
              onClick={() => setSearchText("")}
            >
              <UndoOutlined /> Reset
            </button>
          </div>
        </div>

        {/* Table Section */}
        <div className="ss-table-wrap">
          <DataTable
            columns={columns}
            data={filteredData}
            rowKey="_id"
            locale={{
              emptyText: (
                <EmptyState
                  title="No Sales Data"
                  description={searchText
                    ? "No records match your search."
                    : "Track your offline sales here. Add a record to get started."
                  }
                  gradient="pink"
                  onAction={!searchText ? handleAddRecord : undefined}
                  actionText="Add Sales Record"
                  icon="inbox"
                />
              )
            }}
            loading={loading}
            pagination={{
              current: page,
              pageSize: pageSize,
              total: totalSold,
              onChange: handlePageChange,
              onShowSizeChange: onShowSizeChange
            }}
          />
        </div>

        {/* Detailed Record View Modal */}
        <Modal
          open={detailsVisible}
          onCancel={() => setDetailsVisible(false)}
          footer={null}
          width={550}
          closable={true}
          className="ss-modal"
          centered
        >
          {selectedRecord && (
            <div className="ss-modal-body">
              <div className="ss-modal-hero" style={{ 
                background: selectedRecord.isOnline ? 'linear-gradient(135deg, #e0f2fe, #bae6fd)' : 'linear-gradient(135deg, #fff1f0, #ffccc7)' 
              }}>
                <div className="ss-modal-img">
                  <img
                    src={selectedRecord.productId?.imageUrl?.startsWith('http') ? selectedRecord.productId.imageUrl : getImageUrl(selectedRecord.productId?.imageUrl)}
                    alt={selectedRecord.productId?.name}
                    style={{ width: 90, height: 90, objectFit: 'cover' }}
                  />
                </div>
                <div className="ss-modal-info">
                  <h2 className="ss-modal-name">{selectedRecord.productId?.name}</h2>
                  <p className="ss-modal-id" style={{ 
                    color: selectedRecord.isOnline ? '#0369a1' : '#ff4d4f', 
                    fontWeight: 600 
                  }}>{selectedRecord.isOnline ? 'Online Delivered Order' : 'Sales Record'}</p>
                  <div className="ss-modal-tags">
                    <span className="ss-tag ss-tag-blue">Qty: {selectedRecord.quantity}</span>
                    <span className="ss-tag ss-tag-orange">₹{selectedRecord.totalPrice?.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              <div className="ss-modal-details">
                <div className="pcu-padding-x-24 pcu-padding-top-12">
                  <h4 style={{ 
                    fontSize: 11, 
                    fontWeight: 700, 
                    color: selectedRecord.isOnline ? '#0369a1' : '#ff4d4f', 
                    textTransform: 'uppercase', 
                    marginBottom: 8, 
                    letterSpacing: 0.5 
                  }}>
                    Customer & Transaction
                  </h4>
                </div>
                <div className="ss-modal-row">
                  <div className="ss-modal-label">Customer</div>
                  <div className="ss-modal-value">{selectedRecord.customerName}</div>
                </div>
                <div className="ss-modal-row">
                  <div className="ss-modal-label">Phone</div>
                  <div className="ss-modal-value">{selectedRecord.customerPhone || "N/A"}</div>
                </div>
                <div className="ss-modal-row">
                  <div className="ss-modal-label">Date Sold</div>
                  <div className="ss-modal-value">{new Date(selectedRecord.soldAt).toLocaleString()}</div>
                </div>

                <div className="pcu-padding-x-24 pcu-padding-top-16">
                  <h4 style={{ fontSize: 11, fontWeight: 700, color: '#4776E6', textTransform: 'uppercase', marginBottom: 8, letterSpacing: 0.5 }}>
                    Product & Shop Details
                  </h4>
                </div>
                <div className="ss-modal-row">
                  <div className="ss-modal-label">Category</div>
                  <div className="ss-modal-value">{selectedRecord.productId?.categoryId?.name || "Not Specified"}</div>
                </div>
                <div className="ss-modal-row">
                  <div className="ss-modal-label">Brand</div>
                  <div className="ss-modal-value">{selectedRecord.productId?.brandId?.name || "Not Specified"}</div>
                </div>
                <div className="ss-modal-row">
                  <div className="ss-modal-label">Sold From</div>
                  <div className="ss-modal-value">
                    <div style={{ fontWeight: 600 }}>{selectedRecord.shopId?.name || "Direct Sale"}</div>
                    {selectedRecord.shopId?.location && (
                      <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 400 }}>
                        {[selectedRecord.shopId.location.address, selectedRecord.shopId.location.city].filter(Boolean).join(", ")}
                      </div>
                    )}
                  </div>
                </div>

                <div className="ss-modal-row">
                  <div className="ss-modal-label">Notes</div>
                  <div className="ss-modal-value" style={{ fontWeight: 400, color: '#6b7280' }}>
                    {selectedRecord.notes || "No additional notes provided."}
                  </div>
                </div>
              </div>

              <div className="ss-modal-footer">
                <button className="ss-modal-close-btn" onClick={() => setDetailsVisible(false)}>
                  Close
                </button>
              </div>
            </div>
          )}
        </Modal>

        {/* Modal */}
        {addProductSold && (
          <AddProductSold
            open={addProductSold}
            onClose={() => setAddProductSold(false)}
            productSoldDetails={productSoldDetails}
            isEditing={isEditing}
            refreshProductsSold={refreshProductsSold}
          />
        )}
      </div>
    </div>
  );
};

export default ProductsSold;

